/**
 * Firm-branded self-signup (Client Model B) — slug resolution + survival across
 * the email-confirm round-trip (2026-06-22).
 *
 * A client can land on `/auth/signup` from a firm's branded link two ways:
 *   1. `?firm=<slug>` on any host (e.g. `app.elreign.io/auth/signup?firm=acacia`).
 *   2. The firm's subdomain (`acacia.elreign.io/auth/signup`) — resolved by the
 *      shared `resolveTenantSlugFromHost`.
 *
 * The signup → confirm flow leaves the SPA (Supabase emails a magic link; the
 * user clicks it in their mail client, which opens `/auth/callback` on a fresh
 * navigation). The slug must therefore survive OUTSIDE component state. We
 * persist it to localStorage at signup time and read it back on the callback so
 * `self-join` can attach the confirmed client to the right firm.
 *
 * `?firm=` is also propagated onto Supabase's `emailRedirectTo` query so a
 * cross-DEVICE confirm (user opens the link on their phone, where localStorage
 * is empty) still recovers the slug from the callback URL.
 *
 * The slug is validated against the SAME rule the backend + tenant resolver use,
 * so a junk/spoofed `?firm=` value is dropped (never persisted, never sent).
 */

/** Lowercase tenant slug: 2–63 alnum + hyphens (no leading/trailing hyphen), or
 *  a single char. Mirrors `tenant.ts` SLUG_RE + the backend `SelfJoinSchema`. */
const SLUG_RE = /^[a-z0-9][a-z0-9-]{0,61}[a-z0-9]$|^[a-z0-9]$/;

/** localStorage key holding the pending firm slug across the confirm round-trip. */
export const FIRM_SLUG_STORAGE_KEY = 'elreign:self-signup:firm-slug';

/** Validate + normalise a candidate slug. Returns the lowercased slug or null. */
export function normaliseFirmSlug(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const slug = raw.trim().toLowerCase();
  if (!SLUG_RE.test(slug)) return null;
  return slug;
}

/**
 * Resolve the firm slug for a signup view: the explicit `?firm=<slug>` query
 * param wins, else fall back to the provided host-resolved slug (subdomain).
 * Both are validated. Returns null when neither yields a valid slug.
 *
 * @param search       current URLSearchParams (for `?firm=`)
 * @param hostSlug     slug already resolved from the host (subdomain), or null
 */
export function resolveSignupFirmSlug(
  search: URLSearchParams | null | undefined,
  hostSlug: string | null,
): string | null {
  const fromQuery = normaliseFirmSlug(search?.get('firm') ?? null);
  if (fromQuery) return fromQuery;
  return normaliseFirmSlug(hostSlug);
}

/** Persist the pending firm slug so it survives the email-confirm navigation.
 *  No-op (and swallows) when localStorage is unavailable. */
export function persistPendingFirmSlug(slug: string | null | undefined): void {
  const normalised = normaliseFirmSlug(slug);
  if (typeof window === 'undefined') return;
  try {
    if (normalised) {
      window.localStorage.setItem(FIRM_SLUG_STORAGE_KEY, normalised);
    }
  } catch {
    /* private mode / quota — self-join just won't fire; non-fatal */
  }
}

/**
 * Read the pending firm slug after a confirm. Prefers the callback URL's
 * `?firm=` (survives a cross-device confirm where localStorage is empty), then
 * falls back to the persisted value. Returns null when neither is a valid slug.
 */
export function readPendingFirmSlug(search?: URLSearchParams | null): string | null {
  const fromQuery = normaliseFirmSlug(search?.get('firm') ?? null);
  if (fromQuery) return fromQuery;
  if (typeof window === 'undefined') return null;
  try {
    return normaliseFirmSlug(window.localStorage.getItem(FIRM_SLUG_STORAGE_KEY));
  } catch {
    return null;
  }
}

/** Clear the persisted slug once self-join has run (or been abandoned). */
export function clearPendingFirmSlug(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(FIRM_SLUG_STORAGE_KEY);
  } catch {
    /* non-fatal */
  }
}

/**
 * Build the `emailRedirectTo` URL for a branded signup so the slug rides the
 * magic link (cross-device recovery). Returns the plain callback URL when there
 * is no slug, so non-branded signups are byte-for-byte unchanged.
 *
 * @param origin   window.location.origin
 * @param slug     the (already-validated) firm slug, or null
 */
export function buildSignupRedirectUrl(origin: string, slug: string | null): string {
  const base = `${origin}/auth/callback`;
  const normalised = normaliseFirmSlug(slug);
  if (!normalised) return base;
  return `${base}?firm=${encodeURIComponent(normalised)}`;
}
